import React, { useState, useEffect } from "react";
import { Pagination } from "@mui/material";
import dayjs from "dayjs";
import styles from "./UserManagement.module.scss";
import LayoutHeader from "@features/common/header/layoutHeader/LayoutHeader";
import UserManagementTable from "@features/userManagement/userManagementTable/UserManagementTable";
import {
  useBlockUsersMutation,
  useFetchUsersQuery,
  useUnblockUsersMutation,
} from "@features/userManagement/userManagementAPI";
import { useAppDispatch, useAppSelector } from "@redux/hooks";
import { notifyUser, selectDashboardData } from "@redux/slices/CommonSlice";
import API_URLS, { API_BASE_URL } from "@constants/apiUrls";
import { selectAccessToken } from "@redux/slices/AuthSlice";


const UserManagement = () => {
  // ============================================================================
  // Redux Hooks
  // ============================================================================
  const dispatch = useAppDispatch();
  const accessToken = useAppSelector(selectAccessToken);
  const dashboardData = useAppSelector(selectDashboardData);

  // ============================================================================
  // Local State
  // ============================================================================
  const [currentPage, setCurrentPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("");
  const [selectedActivity, setSelectedActivity] = useState<string | null>(null);
  const [exporting, setExporting] = useState(false);

  // ============================================================================
  // API Queries & Mutations
  // ============================================================================
  /**
   * Fetches paginated list of users with search and filter params
   */
  const { data, isFetching } = useFetchUsersQuery(
    {
      page: currentPage,
      q: debouncedSearchTerm,
      status: selectedActivity || "all",
    },
    { refetchOnMountOrArgChange: true }
  );

  /**
   * Mutation hooks for blocking and unblocking users
   */
  const [blockUsers, { isLoading: isBlockLoading }] = useBlockUsersMutation();
  const [unblockUsers, { isLoading: isUnblockLoading }] =
    useUnblockUsersMutation();

  // ============================================================================
  // Effects
  // ============================================================================
  /**
   * Debounce search term to avoid excessive API calls.
   * Updates debouncedSearchTerm 500ms after user stops typing.
   */
  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 500);

    return () => clearTimeout(handler);
  }, [searchTerm]);

  // ============================================================================
  // Event Handlers
  // ============================================================================
  /**
   * Handles search input change
   * Resets to first page when search term changes
   *
   * @param {string} value - The new search term
   */
  const handleSearchChange = (value: string) => {
    setSearchTerm(value);
    setCurrentPage(1);
  };

  /**
   * Handles activity filter change
   * Resets to first page when filter changes
   *
   * @param {string | null} value - The selected activity filter (all/active/inactive)
   */
  const handleSelectedActivityChange = (value: string | null) => {
    setSelectedActivity(value);
    setCurrentPage(1);
  };

  /**
   * Handles blocking selected users
   * Displays success/error notifications
   *
   * @param {number[]} userIds - Array of user IDs to block
   * @throws {Error} Re-throws error for table component to handle
   */
  const handleBlockUsers = async (userIds: number[]) => {
    try {
      await blockUsers({ users: userIds }).unwrap();
      dispatch(
        notifyUser({
          message: "Selected Users blocked.",
          title: "Users Blocked",
        })
      );
    } catch (error: any) {
      if (error?.status === 400) {
        dispatch(
          notifyUser({
            message: error?.data?.message || "Failed to block users.",
            title: "Block Error",
            severity: "error",
          })
        );
      }

      throw error; // Re-throw to let the table component handle it
    }
  };

  /**
   * Handles unblocking selected users
   * Displays success/error notifications
   *
   * @param {number[]} userIds - Array of user IDs to unblock
   * @throws {Error} Re-throws error for table component to handle
   */
  const handleUnblockUsers = async (userIds: number[]) => {
    try {
      await unblockUsers({ users: userIds }).unwrap();
      dispatch(
        notifyUser({
          message: "Selected Users unblocked.",
          title: "Users Unblocked",
        })
      );
    } catch (error: any) {
      if (error?.status === 400) {
        dispatch(
          notifyUser({
            message:
              error?.data?.message ||
              "Failed to unblock user, Contact support.",
            title: "Unblock Error",
            severity: "error",
          })
        );
      }

      throw error; // Re-throw to let the table component handle it
    }
  };

  /**
   * Handles exporting the user list
   * Triggers download of the user list as a CSV file
   * Displays success/error notifications
   */
  /**
   * Handles exporting the user list
   * Triggers download of the user list as a CSV file
   * Displays success/error notifications
   */
  const handleExport = async () => {
    setExporting(true);

    try {
      const params = new URLSearchParams({
        ...(searchTerm && { q: searchTerm }),
        ...(selectedActivity && { status: selectedActivity }),
      }).toString();

      const response = await fetch(
        `${
          API_BASE_URL
        }${API_URLS.EXPORT_USERS}?${params}`,
        {
          method: "GET",
          credentials: "include",
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
        }
      );

      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute(
          "download",
          `credits-users-export-${dayjs().format("DD-MMM-YYYY-HHmm")}.csv`
        );
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);

        dispatch(
          notifyUser({
            message: "Successfully exported the users.",
            title: "Export Success",
          })
        );
      } else {
        // Handle error response
        let errorMessage = "Users export failed.";
        try {
          const errorData = await response.json();
          errorMessage = errorData?.message || errorMessage;
        } catch (jsonError) {
          // If response is not JSON, use default error message
          console.error("Error parsing error response:", jsonError);
        }

        dispatch(
          notifyUser({
            message: errorMessage,
            title: "Export Error",
            severity: "error",
          })
        );
      }
    } catch (err) {
      console.error("Export error:", err);
      dispatch(
        notifyUser({
          message: err instanceof Error ? err.message : "Users export failed.",
          title: "Export Error",
          severity: "error",
        })
      );
    } finally {
      // Always reset loading state, regardless of success or failure
      setExporting(false);
    }
  };

  // ============================================================================
  // Render
  // ============================================================================
  return (
    <div className={styles.pageContainer}>
      {/* Page Header with Credit Balance */}
      <LayoutHeader
        headerTitle="User Management"
        currency={dashboardData?.currency}
        amount={dashboardData?.amount}
      />

      {/* User Management Table with Search, Filters and Actions */}
      <UserManagementTable
        users={data?.data}
        isLoading={isFetching}
        searchTerm={searchTerm}
        onSearchChange={handleSearchChange}
        selectedActivity={selectedActivity}
        onselectedActivityChange={handleSelectedActivityChange}
        onBlockUsers={handleBlockUsers}
        onUnblockUsers={handleUnblockUsers}
        onExportUsers={handleExport}
        exporting={exporting}
        isBlockLoading={isBlockLoading}
        isUnblockLoading={isUnblockLoading}
      />

      {/* Pagination Controls */}
      <div
        className={`${styles["pagination-container"]} report-pagination-container`}
      >
        <span className={styles["pagination-container__rows-per-page"]}>
          Rows Per Page: {(data as any)?.current_page_count || 0}
        </span>
        <Pagination
          count={data?.total_page || 1}
          page={currentPage}
          onChange={(_, page) => setCurrentPage(page)}
          color="primary"
          shape="rounded"
          className="compact-pagination"
        />
        <span className={styles["pagination-container__total-pages"]}>
          of {data?.total_page || 0} pages
        </span>
      </div>
    </div>
  );
};

export default UserManagement;
